import { useState } from "react";
import {
  Box,
  Drawer,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { ChevronRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Block } from "../../../../shared";
import {
  getCurrentLanguage,
  setLanguage,
  SUPPORTED_LANGS,
  type SupportedLang,
} from "../../../../../i18n";
import { saveLanguagePreference } from "../../../../../services/me";

interface LangOption {
  code: SupportedLang;
  /** Название языка на самом языке — так пользователь всегда понимает, что видит. */
  native: string;
  /** Флаг-эмодзи (Iconify circle-flags), опционально. */
  flag: string;
}

const OPTIONS: LangOption[] = [
  { code: "ru", native: "Русский", flag: "circle-flags:ru" },
  { code: "en", native: "English", flag: "circle-flags:us" },
];

/**
 * Переключатель языка интерфейса. Открывается bottom-sheet'ом со списком
 * поддерживаемых языков. Выбор:
 *   • мгновенно применяется к UI (react-i18next),
 *   • сохраняется в localStorage,
 *   • асинхронно летит на бэк (`PATCH /me`), чтобы бот и e-mail-нотификации
 *     тоже шли на выбранном языке. Ошибку сохранения глотаем тихо.
 */
export function LanguageBlock() {
  const { t } = useTranslation("profile");
  const { enqueueSnackbar } = useSnackbar();
  const [current, setCurrent] = useState<SupportedLang>(getCurrentLanguage());
  const [open, setOpen] = useState(false);

  const currentOption = OPTIONS.find((o) => o.code === current) ?? OPTIONS[0];

  const handleSelect = (lang: SupportedLang) => {
    setCurrent(lang);
    setLanguage(lang);
    setOpen(false);
    void saveLanguagePreference(lang);
    enqueueSnackbar(
      t("language.current", {
        lang:
          OPTIONS.find((o) => o.code === lang)?.native ?? lang.toUpperCase(),
      }),
      { variant: "success" },
    );
  };

  return (
    <>
      <Block sx={{ paddingTop: "8px", paddingBottom: "8px" }}>
        <ListItem
          onClick={() => setOpen(true)}
          sx={{
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingInline: 0,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            cursor: "pointer",
          }}
        >
          <ListItemIcon
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(51,119,255,0.16)"
                  : "rgba(51,119,255,0.10)",
              color: theme.palette.primary.main,
              minWidth: "36px",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "10px",
              alignSelf: "center",
              flexShrink: 0,
            })}
          >
            <Icon icon="solar:global-linear" width={20} height={20} />
          </ListItemIcon>

          <ListItemText
            sx={{ margin: 0, flexGrow: 1 }}
            primary={
              <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
                {t("blocks.language")}
              </Typography>
            }
            secondary={
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  marginTop: "2px",
                }}
              >
                {currentOption.native}
              </Typography>
            }
          />

          <ChevronRight sx={{ color: "#b0b0b0", flexShrink: 0 }} />
        </ListItem>
      </Block>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ zIndex: 100010 }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "secondary.main",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            padding: "10px 16px 28px",
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={(theme) => ({
            width: "44px",
            height: "4px",
            borderRadius: "4px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)",
            margin: "0 auto 14px",
          })}
        />

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            textAlign: "center",
            marginBottom: "16px",
            color: "text.primary",
          }}
        >
          {t("language.title")}
        </Typography>

        <Box sx={{ display: "grid", gap: "8px" }}>
          {SUPPORTED_LANGS.map((code) => {
            const opt = OPTIONS.find((o) => o.code === code);
            if (!opt) return null;
            const active = opt.code === current;
            return (
              <Box
                key={opt.code}
                onClick={() => handleSelect(opt.code)}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 14px",
                  borderRadius: "16px",
                  cursor: "pointer",
                  bgcolor: active
                    ? theme.palette.mode === "dark"
                      ? "rgba(48, 112, 241, 0.14)"
                      : "rgba(48, 112, 241, 0.10)"
                    : theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.04)"
                      : "rgba(0, 0, 0, 0.04)",
                  border: active
                    ? `1px solid ${theme.palette.primary.main}`
                    : "1px solid transparent",
                  transition: "background 0.15s, border-color 0.15s",
                  "&:active": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(0, 0, 0, 0.06)",
                  },
                })}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon icon={opt.flag} width={32} height={32} />
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "15px",
                    fontWeight: active ? 600 : 500,
                    color: "text.primary",
                  }}
                >
                  {opt.native}
                </Typography>
                {active && (
                  <Icon
                    icon="solar:check-circle-bold"
                    width={22}
                    height={22}
                    style={{ color: "var(--mui-palette-primary-main, #3070F1)" }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Drawer>
    </>
  );
}
